import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import AppShell from '@/components/AppShell';
import { Building2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const ORG_TYPES = [
  { value: 'fpo', label: 'Farmer Producer Organization (FPO)' },
  { value: 'cooperative', label: 'Cooperative' },
  { value: 'aggregator', label: 'Aggregator' },
  { value: 'company', label: 'Private Company' },
];

export default function OrgRegisterPage() {
  const { api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '',
    org_type: 'fpo',
    contact_email: '',
    address: '',
    gstin: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Organization name is required');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post('/orgs/register', form);
      toast.success(`Application submitted! Your org code is ${data.organization.code}. Awaiting Frootex approval.`);
      await refreshUser();
      navigate('/org');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppShell
      title="Register your organization"
      showNav={false}
      headerLeft={
        <button
          data-testid="org-register-back-btn"
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full bg-[#EFF2F7] hover:bg-[#E4E8EF] flex items-center justify-center transition-colors"
          aria-label="Go back"
        >
          <ArrowLeft className="w-4 h-4 text-[#0B1620]" />
        </button>
      }
    >
      <div className="p-4" data-testid="org-register-page">
        <div className="bg-[#0B4F3F]/5 border border-[#0B4F3F]/20 rounded-2xl p-4 mb-5 flex gap-3">
          <Building2 className="w-5 h-5 text-[#0B4F3F] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-[#0B1620] font-['Bricolage_Grotesque']">Become an Enterprise on Frootex</p>
            <p className="text-xs text-[#5B6675] mt-1">Group multiple farmers under one umbrella. Get analytics, manage your members, and let Frootex handle vendor demand.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 bg-[#F6F8FB] rounded-2xl border border-[#E4E8EF] p-4">
          <div>
            <Label className="text-xs">Organization name *</Label>
            <Input
              data-testid="org-register-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g., Green Valley FPO"
              className="h-10 bg-[#F6F8FB] border-[#E4E8EF] rounded-lg text-sm mt-1"
            />
          </div>

          <div>
            <Label className="text-xs">Type</Label>
            <Select value={form.org_type} onValueChange={(v) => setForm({ ...form, org_type: v })}>
              <SelectTrigger data-testid="org-register-type" className="h-10 bg-[#F6F8FB] border-[#E4E8EF] rounded-lg text-sm mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#F6F8FB] border-[#E4E8EF]">
                {ORG_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Contact email</Label>
            <Input
              data-testid="org-register-email"
              type="email"
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              placeholder="org@example.com"
              className="h-10 bg-[#F6F8FB] border-[#E4E8EF] rounded-lg text-sm mt-1"
            />
          </div>

          <div>
            <Label className="text-xs">Address</Label>
            <Input
              data-testid="org-register-address"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="Village, District, State"
              className="h-10 bg-[#F6F8FB] border-[#E4E8EF] rounded-lg text-sm mt-1"
            />
          </div>

          <div>
            <Label className="text-xs">GSTIN (optional)</Label>
            <Input
              data-testid="org-register-gstin"
              value={form.gstin}
              onChange={(e) => setForm({ ...form, gstin: e.target.value })}
              placeholder="22AAAAA0000A1Z5"
              className="h-10 bg-[#F6F8FB] border-[#E4E8EF] rounded-lg text-sm mt-1"
            />
          </div>

          <Button
            data-testid="org-register-submit"
            type="submit"
            disabled={submitting}
            className="w-full h-11 bg-[#0B4F3F] hover:bg-[#083D31] text-[#F6F8FB] rounded-full font-semibold"
          >
            {submitting ? 'Submitting…' : 'Submit application'}
          </Button>
          <p className="text-[11px] text-[#5B6675] text-center">
            After submission, the Frootex team will review and approve your org. You&apos;ll be notified once active.
          </p>
        </form>
      </div>
    </AppShell>
  );
}
